import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationContext } from '@client/context/NotificationContext';
import { TNotificationConfig } from '@client/types';
import { logger } from '@client/utils/logger.util';

export const useScript = (
  url: string,
  loaded: boolean = false,
  scriptName?: string,
) => {
  const { t } = useTranslation();

  const [isLoaded, setIsLoaded] = useState(() => loaded);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  const { notificationApi } = useContext(NotificationContext);

  useEffect(() => {
    // If already loaded, no need to do anything
    if (isLoaded) {
      return;
    }

    const loadScript = () => {
      if (scriptRef.current) return;

      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        // This is an async callback, so it's fine
        setIsLoaded(true);
        logger.info('Script loaded:', scriptName ?? url);
      };

      script.onerror = async () => {
        notificationApi?.error({
          title: t('error.load.script'),
          description: scriptName ?? url,
          ...TNotificationConfig,
        });

        logger.error('Failed to load script:', scriptName ?? url);
      };

      document.head.appendChild(script);
      scriptRef.current = script;
    };

    loadScript();

    return () => {
      if (scriptRef.current) {
        document.head.removeChild(scriptRef.current);
        scriptRef.current = null;
      }
    };
  }, [isLoaded, notificationApi, scriptName, t, url]);

  return { isLoaded };
};
